<script lang="ts">
  // The ordering of these imports is critical to your app working properly
  // import '../theme.postcss';
  // If you have source.organizeImports set to true in VSCode, then it will auto change this ordering
  //import '@skeletonlabs/skeleton/styles/all.css';
  // Most of your app wide CSS should be put in this file
  import '../app.css';

  import {
    getDrawerStore,
    getModalStore,
    initializeStores
  } from '@skeletonlabs/skeleton';
  import {
    AppShell,
    AppBar,
    TableOfContents,
    tocCrawler,
    Drawer,
    Modal
  } from '@skeletonlabs/skeleton';
  import type { ModalSettings, ModalComponent } from '@skeletonlabs/skeleton';

  import Navigation from '$lib/navigation/Navigation.svelte';
  import { page } from '$app/stores';
  import '$lib/assets/prism-vsc-dark-plus.css';
  import { beforeNavigate, afterNavigate } from '$app/navigation';
  import { fade } from 'svelte/transition';
  import { clickOutside } from '$lib/clickOutside';

  import DocsSearch from './DocsSearch.svelte';

  import github from '$lib/assets/github.svg?raw';
  import kofi from '$lib/assets/ko-fi.svg?raw';
  import paypal from '$lib/assets/paypal.svg?raw';
  import buymeacoffee from '$lib/assets/buymeacoffee.svg?raw';
  import SearchButton from './SearchButton.svelte';
  import { tick } from 'svelte';
  import { writable } from 'svelte/store';
  import copy from 'clipboard-copy';

  import { CandlestickChart } from 'lucide-svelte';
  initializeStores();

  const drawerStore = getDrawerStore();
  const modalStore = getModalStore();

  // Local
  let hideSponsor = true;

  async function drawerOpen() {
    drawerStore.open({});
    await tick();
    if (document.activeElement?.id === 'search-button') {
      (document.activeElement as HTMLButtonElement).blur();
    }
  }

  const modalRegistry: Record<string, ModalComponent> = {
    search: {
      ref: DocsSearch,
      props: {
        results: writable({ term: '', results: [] })
      }
    }
  };

  function triggerSearch(): void {
    const d: ModalSettings = {
      type: 'component',
      component: 'search',
      position: 'item-start'
    };
    modalStore.trigger(d);
  }

  // Keyboard Shortcut (CTRL/⌘+K) to Focus Search
  function onWindowKeydown(e: KeyboardEvent): void {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      // Prevent default browser behavior of focusing URL bar
      e.preventDefault();
      // If modal currently open, close modal (allows to open/close search with CTRL/⌘+K)
      $modalStore.length ? modalStore.close() : triggerSearch();
    }
  }

  const noToC = ['/', '/sponsors'];
  $: displayToC = !noToC.includes($page.url.pathname);

  beforeNavigate((nav) => {
    if (nav.type == 'form') return;
    document
      .querySelectorAll('.copy-content')
      .forEach((el) => el.removeEventListener('click', copyContent));
  });

  afterNavigate((nav) => {
    if (nav.type == 'link') {
      // If linked to a page, sometimes it won't scroll to the top.
      const id = nav.to?.url.hash ? nav.to.url.hash.slice(1) : 'page';
      document.getElementById(id)?.scrollTo(0, 0);
    } else if (nav.type == 'enter' && $page.url.hash) {
      const el = document.getElementById($page.url.hash.substring(1));
      if (el) el.scrollIntoView();
    }

    if (nav.type != 'form') {
      copyBoxes();
    }
  });

  const checkedIcon =
    '<path fill="currentColor" d="m10.6 16.2l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM5 21q-.825 0-1.413-.588T3 19V5q0-.825.588-1.413T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.588 1.413T19 21H5Zm0-2h14V5H5v14ZM5 5v14V5Z"/>';

  async function copyContent(e: Event) {
    if (!e.target) return;
    const parent = (e.target as HTMLElement).closest('pre');
    if (!parent) return;

    const codeEl = parent.querySelector('code');
    const svg = parent.querySelector('.copy-content svg');

    if (codeEl) {
      await copy(codeEl.innerText);
      if (svg) {
        const oldContent = svg.innerHTML;
        svg.innerHTML = checkedIcon;
        setTimeout(() => (svg.innerHTML = oldContent), 800);
      }
    }
  }

  function copyBoxes() {
    document
      .querySelectorAll<HTMLElement>('pre:not(.super-debug--pre) > code')
      .forEach((el) => {
        const pre = el.parentElement as HTMLPreElement;
        var copy = document.createElement('div');
        copy.classList.add('copy-content');
        copy.addEventListener('click', copyContent);

        copy.innerHTML =
          '<button type="button" aria-label="Copy code to clipboard"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M5 22q-.825 0-1.413-.588T3 20V6h2v14h11v2H5Zm4-4q-.825 0-1.413-.588T7 16V4q0-.825.588-1.413T9 2h9q.825 0 1.413.588T20 4v12q0 .825-.588 1.413T18 18H9Zm0-2h9V4H9v12Zm0 0V4v12Z"/></svg></button>';

        pre.prepend(copy);
      });
  }
  // import Meta from '$lib/SEO.svelte';
  import { innerWidth } from '$lib/stores/common';

  // Banner visibility state
  let showBanner = true;

  const handleClick = async (event) => {
    event.preventDefault(); // Prevent the default link behavior

    const body = {
      created_at: new Date().toISOString(),
      action: 'Discord click',
      userAgent: navigator.userAgent
    };

    console.log('calling actionTaken API and saving');

    const res = await fetch('/api/actionTaken', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (res.ok) {
      console.log('success');
      window.location.href = 'https://discord.gg/qcR5zTbJzg';
    } else {
      console.error('Failed to save action');
    }
  };

  function handleSkipNavigationClick(event: Event) {
    event.preventDefault();
    const targetId = (event.target as HTMLAnchorElement)
      .getAttribute('href')
      ?.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.setAttribute('tabindex', '-1');
      targetElement.focus();
      window.scrollTo(0, targetElement.offsetTop);
      history.replaceState(null, '', window.location.pathname); // Reset URL hash
    }
  }
</script>

<!-- <Meta
  title="Comprehensive Data Visualizations with Svelte and D3 | Interactive and Dynamic Charts Tutorial"
  description="Master the art of creating comprehensive and interactive data visualizations using Svelte and D3.js. Learn through detailed tutorials and courses about new features, performance benefits, and advanced techniques to enhance your projects." /> -->

<svelte:window
  on:keydown|stopPropagation={onWindowKeydown}
  bind:innerWidth={$innerWidth} />

<Modal components={modalRegistry} />
<Drawer width="w-70">
  <h3 class="p-4">Navigation</h3>
  <hr />
  <Navigation />
</Drawer>

<!-- Banner Section: Above the main content -->
<!-- Banner Section: Above the main content -->
{#if showBanner}
  <div
    class="hidden lg:block relative overflow-hidden text-white py-2 px-4 shadow-md">
    <!-- Decorative data visualization elements (simplified) -->
    <div class="absolute top-0 left-0 w-full h-full opacity-10">
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 20"
        preserveAspectRatio="none">
        <path
          d="M0,10 Q25,5 50,10 T100,10"
          stroke="white"
          stroke-width="1"
          fill="none" />
        <circle cx="30" cy="10" r="1" fill="white" />
        <circle cx="70" cy="10" r="1" fill="white" />
      </svg>
    </div>

    <div class="flex items-center justify-between gap-2 relative z-10">
      <div class="flex-1 flex items-center justify-center gap-3">
        <p class="text-sm text-white">
          Transform your data into <span class=""
            >compelling visual stories</span
          >!
        </p>

        <a
          href="https://revealthestory.com"
          target="_blank"
          class="inline-flex items-center gap-1 text-sm text-yellow-500 hover:text-yellow-400 px-2 py-0.5 rounded transition-colors duration-200">
          <span>Explore RevealTheStory.com</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M7 17l9.2-9.2M17 17V7H7" />
          </svg>
        </a>
      </div>

      <!-- <button
        on:click={() => (showBanner = false)}
        class="text-white/80 hover:text-white transition-colors duration-200"
        aria-label="Close banner">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button> -->
    </div>
  </div>
{/if}

<!-- App Shell -->
<AppShell regionPage="shrink-0" slotSidebarLeft="bg-surface-500/5 w-0 lg:w-56">
  <svelte:fragment slot="header">
    <!-- App Bar -->
    <AppBar>
      <svelte:fragment slot="lead">
        <div class="flex shrink-0 items-center">
          <!-- <a
            class="absolute left-0 m-3 -translate-y-16 bg-primary-500 p-3 text-black transition focus:translate-y-0"
            href="#page-content"
            on:click={handleSkipNavigationClick}>Skip Navigation</a> -->
          <button
            aria-label="Open navigation menu"
            class="btn btn-sm mr-4 lg:hidden"
            on:click={drawerOpen}>
            <span>
              <svg viewBox="0 0 100 80" class="hamburger h-4 w-4">
                <rect width="100" height="20" />
                <rect y="30" width="100" height="20" />
                <rect y="60" width="100" height="20" />
              </svg>
            </span>
          </button>

          <a href="/" class="mr-3 hidden lg:block" aria-label="Homepage">
            <svg
              fill="#71c4f9"
              id="Warstwa_1"
              data-name="Warstwa 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 331.33 302.75"
              ><rect x="174.93" y="15.81" width="87.26" height="87.26" /><rect
                x="506.44"
                y="400.21"
                width="87.26"
                height="87.26"
                transform="translate(-426.56 41.87) rotate(-30)" /><rect
                x="453.54"
                y="492.15"
                width="87.26"
                height="87.26"
                transform="translate(-493.75 405.82) rotate(-60)" /><rect
                x="69.14"
                y="199.68"
                width="87.26"
                height="87.26" /><rect
                x="294.31"
                y="400.53"
                width="87.26"
                height="87.26"
                transform="translate(-455.14 -64.15) rotate(-30)" /><rect
                x="347.2"
                y="308.6"
                width="87.26"
                height="87.26"
                transform="translate(-387.96 221.95) rotate(-60)" /></svg>
          </a>
          <a href="/" class="truncate text-lg font-bold md:text-xl">
            Using D3 with Svelte
          </a>
        </div>
      </svelte:fragment>
      <svelte:fragment slot="trail">
        <a
          aria-label="join-on-x"
          class="flex mr-2 gap-2 fill-primary-500 text-primary-500"
          href="/login">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="lucide lucide-key-round"
            ><path
              d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" /><circle
              cx="16.5"
              cy="7.5"
              r=".5"
              fill="currentColor" />
          </svg>
          {$page.data.user ? 'Pro' : 'Log In'}
        </a>
        <a
          aria-label="join-on-x"
          class="mr-2 fill-primary-500"
          href="https://twitter.com/datavizsvelte"
          target="_blank"
          rel="noreferrer">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30">
            <path
              d="M26.37,26l-8.795-12.822l0.015,0.012L25.52,4h-2.65l-6.46,7.48L11.28,4H4.33l8.211,11.971L12.54,15.97L3.88,26h2.65 l7.182-8.322L19.42,26H26.37z M10.23,6l12.34,18h-2.1L8.12,6H10.23z" />
          </svg>
        </a>
        <a
          on:click={handleClick}
          aria-label="join-on-discord"
          class="mr-2 fill-primary-500"
          href="https://discord.gg/qcR5zTbJzg"
          target="_blank"
          rel="noreferrer">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 15 15"
            ><path
              fill-rule="evenodd"
              d="M5.075 1.826a.48.48 0 0 0-.127-.003c-.841.091-2.121.545-2.877.955a.48.48 0 0 0-.132.106c-.314.359-.599.944-.822 1.498C.887 4.95.697 5.55.59 5.984C.236 7.394.043 9.087.017 10.693a.48.48 0 0 0 .056.23c.3.573.947 1.104 1.595 1.492c.655.393 1.42.703 2.036.763a.48.48 0 0 0 .399-.153c.154-.167.416-.557.614-.86c.09-.138.175-.27.241-.375c.662.12 1.492.19 2.542.19c1.048 0 1.878-.07 2.54-.19c.066.106.15.237.24.374c.198.304.46.694.615.861a.48.48 0 0 0 .399.153c.616-.06 1.38-.37 2.035-.763c.648-.388 1.295-.919 1.596-1.492a.48.48 0 0 0 .055-.23c-.025-1.606-.219-3.3-.571-4.71a12.98 12.98 0 0 0-.529-1.601c-.223-.554-.508-1.14-.821-1.498a.48.48 0 0 0-.133-.106c-.755-.41-2.035-.864-2.877-.955a.48.48 0 0 0-.126.003a1.18 1.18 0 0 0-.515.238a2.905 2.905 0 0 0-.794.999A14.046 14.046 0 0 0 7.5 3.02c-.402 0-.774.015-1.117.042a2.905 2.905 0 0 0-.794-.998a1.18 1.18 0 0 0-.514-.238Zm5.943 9.712a23.136 23.136 0 0 0 .433.643c.396-.09.901-.3 1.385-.59c.543-.325.974-.7 1.182-1.017c-.033-1.506-.219-3.07-.54-4.358a12.046 12.046 0 0 0-.488-1.475c-.2-.498-.415-.92-.602-1.162c-.65-.337-1.675-.693-2.343-.79a.603.603 0 0 0-.058.04a1.5 1.5 0 0 0-.226.22a2.52 2.52 0 0 0-.113.145c.305.056.577.123.818.197c.684.21 1.177.5 1.418.821a.48.48 0 1 1-.768.576c-.059-.078-.316-.29-.932-.48c-.595-.182-1.47-.328-2.684-.328c-1.214 0-2.09.146-2.684.329c-.616.19-.873.4-.932.479a.48.48 0 1 1-.768-.576c.241-.322.734-.61 1.418-.82c.24-.075.512-.141.816-.197a2.213 2.213 0 0 0-.114-.146a1.5 1.5 0 0 0-.225-.22a.604.604 0 0 0-.059-.04c-.667.097-1.692.453-2.342.79c-.188.243-.402.664-.603 1.162c-.213.53-.39 1.087-.487 1.475c-.322 1.288-.508 2.852-.54 4.358c.208.318.638.692 1.181 1.018c.485.29.989.5 1.386.589a16.32 16.32 0 0 0 .433-.643c-.785-.279-1.206-.662-1.48-1.072a.48.48 0 0 1 .8-.532c.26.392.944 1.086 4.2 1.086c3.257 0 3.94-.694 4.2-1.086a.48.48 0 0 1 .8.532c-.274.41-.696.794-1.482 1.072ZM4.08 7.012c.244-.262.575-.41.92-.412c.345.002.676.15.92.412c.243.263.38.618.38.988s-.137.725-.38.988c-.244.262-.575.41-.92.412a1.263 1.263 0 0 1-.92-.412A1.453 1.453 0 0 1 3.7 8c0-.37.137-.725.38-.988ZM10 6.6c-.345.002-.676.15-.92.412c-.243.263-.38.618-.38.988s.137.725.38.988c.244.262.575.41.92.412c.345-.002.676-.15.92-.412c.243-.263.38-.618.38-.988s-.137-.725-.38-.988A1.263 1.263 0 0 0 10 6.6Z"
              clip-rule="evenodd" /></svg>
        </a>
        <!-- <a
          href="https://github.com/"
          class="w-7 text-primary-500 md:w-8 md:pr-2"
          target="_blank"
          rel="noreferrer"
          >{@html github}
        </a> -->
        <SearchButton id={'search-mobile'} cls="hidden md:inline" />
        <button
          type="button"
          class="sponsor variant-ghost btn btn-sm relative text-primary-500"
          on:click={() => (hideSponsor = !hideSponsor)}>
          <span
            ><svg
              class="fill-yellow-400"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              ><path
                d="m12 21.35l-1.45-1.32C5.4 15.36 2 12.27 2 8.5C2 5.41 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.08C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.41 22 8.5c0 3.77-3.4 6.86-8.55 11.53L12 21.35Z" /></svg
            ></span>
          <span class="hidden md:inline">Donate</span>
        </button>
      </svelte:fragment>
    </AppBar>
    {#if !hideSponsor}
      <div
        transition:fade|global={{ duration: 150 }}
        class="sponsor h-50 card absolute flex flex-col justify-end gap-3 p-4 md:h-[172px]"
        use:clickOutside={{
          event: () => (hideSponsor = !hideSponsor),
          ignore: '.sponsor.btn'
        }}>
        <div class="relative -left-5 -top-1 w-28 text-center md:hidden">
          Donate
        </div>

        <a
          href="https://buymeacoffee.com/dataatomic"
          target="_blank"
          class="variant-ghost btn btn-sm">
          <span class="w-6 p-0 text-primary-500">{@html buymeacoffee}</span>
          <span>Buymeacoffee</span>
        </a>
        <a
          href="https://github.com/sponsors/korur"
          target="_blank"
          class="variant-ghost btn btn-sm flex justify-start">
          <span class="w-7 p-1 text-primary-500">{@html github}</span>
          <span>Github</span>
        </a>
        <!-- <a
          href="https://ko-fi.com/ciscoheat"
          target="_blank"
          class="variant-ghost btn btn-sm">
          <span class="w-6 p-0 text-primary-500">{@html kofi}</span>
          <span>Ko-fi</span>
        </a> -->

        <!-- <form
          class="flex"
          action="https://www.paypal.com/donate"
          method="POST"
          target="_top">
          <input type="hidden" name="hosted_button_id" value="NY7F5ALHHSVQS" />
          <button type="submit" class="variant-ghost btn btn-sm w-full">
            <span class="w-6 p-0 text-primary-500">{@html paypal}</span>
            <span class="pr-3">Paypal</span>
          </button>
        </form> -->
      </div>
    {/if}
  </svelte:fragment>
  <svelte:fragment slot="sidebarLeft">
    <Navigation />
  </svelte:fragment>
  <!-- Page Route Content -->
  <div
    use:tocCrawler={{
      mode: 'generate',
      scrollTarget: '#page',
      key: $page.url.pathname
    }}>
    <main class="#page-content pb-24">
      <slot />
    </main>
  </div>
  <svelte:fragment slot="sidebarRight">
    <div class="hidden md:block w-56 p-4">
      <TableOfContents class={displayToC ? 'block' : 'invisible'} />
    </div>
  </svelte:fragment>
</AppShell>

<style lang="scss">
  .hamburger {
    fill: #71c4f9;
  }

  svg {
    color: #71c4f9;
    width: 24px;
    height: 24px;
  }

  img.logo {
    width: 36px;
    height: 36px;
  }

  .sponsor.btn {
    z-index: 4;
  }

  .sponsor.card {
    top: 10px;
    right: 5px;
  }

  :global(pre:hover .copy-content) {
    visibility: visible;
  }

  :global(.copy-content) {
    float: right;
    color: #84792e;
    visibility: hidden;
  }
</style>
